import "./styles/OverviewPage.scss"
import React, {useEffect, useState} from "react";
import Tooltip from "@atlaskit/tooltip";
import {navigationService} from "../../api/NavigationService";
import {Menu} from "navigation-common/types/navigation-types";
import {useFlags} from "@atlaskit/flag";
import EditorPanelIcon from '@atlaskit/icon/glyph/editor/panel';
import ConfigSidebar from "./ConfigSidebar";
import {SelectOption} from "navigation-common/types/html-types";
import {confluenceService} from "../../api/ConfluenceService";
import {Label} from "navigation-common/types/confluence-types";
import {errorFlag, successFlag} from "../../model/flags";
import {OverviewPageActions} from "./OverviewPageActions";
import {AppLink} from "./AppLink";
import {PAGE_ADMIN_MENU_SETTINGS} from "navigation-common/types/pages-constants";
import SectionMessage from "@atlaskit/section-message";
import {OverviewMenuEntry} from "./OverviewMenuEntry";
import {useAsyncFn, useEffectOnce} from "react-use";

function OverviewPage() {
    const [menus, setMenus] = useState<Array<Menu>>([]);
    const [editMenuMetaData, setEditMenuMetaData] = React.useState<Menu | null>(null);
    const [allSpaceLabels, setAllSpaceLabels] = React.useState<Array<SelectOption>>([]);
    const [anonymousAccessAssigned, setAnonymousAccessAssigned] = React.useState(false);
    const [isDefaultMenuMissing, setIsDefaultMenuMissing] = React.useState(false);
    const [isSidebarOpen, setIsSidebarOpen] = React.useState<boolean>(false);
    const {showFlag} = useFlags();

    AP.sizeToParent(true);

    const [fetchAllMenusState, fetchAllMenus] = useAsyncFn(async (syncSpaceLabel: boolean) => {
        const loadedMenus = await navigationService.loadAllMenus();

        if (syncSpaceLabel) {
            const existingSpaceLabels = await confluenceService.getAllSpaceLabels();
            await Promise.all(loadedMenus.map(menu => syncSpaceLabels(menu, existingSpaceLabels)));
            setAllSpaceLabels(existingSpaceLabels.map(label => ({value: label.id, label: label.name})));
        }

        setMenus(loadedMenus);
        checkIfDefaultMenuIsMissing(loadedMenus);
    }, []);

    useEffectOnce(() => {
        fetchAllMenus(true);
    });

    useEffect(() => {
        updateAnonymousAccessAssigned();
    }, [menus]);

    const checkIfDefaultMenuIsMissing = (allMenus: Array<Menu>) => {
        const hasDefaultMenu = allMenus.some(menu => menu.default);
        setIsDefaultMenuMissing(!hasDefaultMenu);
    }

    const createDemoMenu = async () => {
        try {
            await navigationService.createDemoMenu();
        } catch (e: any) {
            console.error("Create demo menu failed.", e);
            return handleOperationError(e);
        }

        return fetchAllMenus(false);
    }

    const createEmptyMenu = async () => {
        try {
            await navigationService.createEmptyMenu(menus.length === 0);
        } catch (e: any) {
            console.error("Create empty menu failed.", e);
            return handleOperationError(e);
        }

        return fetchAllMenus(false);
    }

    const copyMenu = async (menu: Menu) => {
        try {
            await navigationService.copyMenu(menu);
        } catch (e: any) {
            console.error("Copy menu failed.", e.message);
            handleOperationError(e);
            return Promise.reject(e);
        }
        await fetchAllMenus(false);
    }

    const onDeleteMenu = async (menu: Menu) => {
        try {
            await navigationService.deleteMenu(menu);
        } catch (e: any) {
            console.error("Delete menu failed.", e);
            return handleOperationError(e);
        }

        await fetchAllMenus(false);
        showFlag(successFlag({title: `Success!`, description: `The menu "${menu.name}" has been deleted.`}));
    }

    const updateAnonymousAccessAssigned = () => {
        const anonymousAccessExists: Menu | undefined = menus.find((menu) => menu.anonymousAccess === true);
        setAnonymousAccessAssigned(anonymousAccessExists != undefined);
    }

    const updateMenuData = async (updatedMenu: Menu, silent = false) => {
        try {
            await navigationService.updateMenu(updatedMenu);
        } catch (e: any) {
            console.error("Update menu failed.", e);
            return handleOperationError(e);
        }

        await fetchAllMenus(false);
        if (!silent) {
            showFlag(successFlag({title: `Success!`, description: `The menu "${updatedMenu.name}" has been updated.`}));
        }
    };

    const triggerColorChange = async (editedMenu: Menu, newColor: string, darkTheme: boolean) => {
        if (darkTheme) {
            editedMenu.primaryColorDarkTheme = newColor;
        } else {
            editedMenu.primaryColor = newColor;
        }

        return updateMenuData(editedMenu, true);
    }

    const triggerSubMenuBgColorChange = async (editedMenu: Menu, newColor: string, darkTheme: boolean) => {
        if (darkTheme) {
            editedMenu.subMenuBgColorDarkTheme = newColor;
        } else {
            editedMenu.subMenuBgColor = newColor;
        }

        return updateMenuData(editedMenu, true);
    }

    const triggerGroupsChange = async (menu: Menu, groups: Array<string>) => {
        menu.userGroups = groups;
        return updateMenuData(menu, true);
    }

    const triggerSpaceLabelsChange = async (menu: Menu, spaceLabels: Array<string>) => {
        menu.spaceLabels = spaceLabels;
        return updateMenuData(menu, true);
    };

    const triggerActiveChange = async (menu: Menu, active: boolean) => {
        menu.active = active;
        return updateMenuData(menu, true);
    }

    const triggerAnonymousAccessChange = async (menu: Menu, active: boolean) => {
        menu.anonymousAccess = active;
        return updateMenuData(menu, true);
    }

    const changeDefaultMenu = async (menu: Menu) => {
        try {
            await navigationService.changeDefaultMenu(menus, menu);
        } catch (e: any) {
            console.error("Change default menu failed.", e);
            return handleOperationError(e);
        }

        await fetchAllMenus(false);
        showFlag(successFlag({title: `Success!`, description: `Default menu changed to "${menu.name}".`}));
    }

    const handleOperationError = (error: any) => {
        const errMsg = error.response?.data ?? "There was an error. Please try again later.";
        showFlag(errorFlag({title: "Error!", description: errMsg}));
    }

    const openSidebar = (menu: Menu) => {
        setEditMenuMetaData(menu);
        setIsSidebarOpen(true);
    }

    const closeSidebar = () => {
        setIsSidebarOpen(false);
        setTimeout(() => {
            setEditMenuMetaData(null);
        }, 300);
    }

    return <>
        <div id={"atl-cloud-navigation"} className={"ac-content menu-overview desktop-view"}>
            <div className={"admin-heading-wrapper "}>
                <h1 className="admin-heading">Menu overview</h1>
                <div>
                    <OverviewPageActions onMenuImported={copyMenu} showCreateDemoMenu={!fetchAllMenusState.loading && menus.length === 0}
                                         onClickCreate={createEmptyMenu}
                                         onClickCreateDemo={createDemoMenu}/>
                </div>
            </div>

            <div className={"desc"}>
                <p>Create multiple menus with different structures & styles.<br/>
                    You can then easily assign each menu to different Confluence user groups and space categories to display the most suitable menu content for
                    your users.</p>
                <p><b>Tip:</b> You can grant groups permission to edit menus under <AppLink to={PAGE_ADMIN_MENU_SETTINGS.key}>Menu
                    Settings.</AppLink></p>
            </div>

            {isDefaultMenuMissing && <div className={"warning"}>
                <SectionMessage appearance={"warning"}>
                    <p><b>Warning:</b> There is no default menu set! If no group or space category assignment is matching, no menu will be displayed for users.
                    </p>
                </SectionMessage>
            </div>}

            <div className={"headline structure"}>
                <h4>Available menus</h4>
            </div>

            <div className={"nav-admin-group tablehead"}>
                <span className={"nav-admin-item default"}>
                    Default
                    <Tooltip content={"Default menu is displayed when no group or space category assignment is matching"} position={"top"}>
                        <div className={"headline-info"}><EditorPanelIcon label={"info"} size={"medium"}/></div>
                    </Tooltip>
                </span>
                <span className={"nav-admin-item name"}>
                    Name
                </span>
                <span className={"nav-admin-item groups"}>
                    Group assignment
                    <Tooltip content={"Menu(s) will only be visible for users of the assigned group(s)"} position={"top"}>
                        <div className={"headline-info"}><EditorPanelIcon label={"info"} size={"medium"}/></div>
                    </Tooltip>
                </span>
                <span className={"nav-admin-item space-labels"}>
                    Space category assignment
                    <Tooltip content={"Menu(s) will only be display in spaces with the assigned category(s)"} position={"top"}>
                        <div className={"headline-info"}><EditorPanelIcon label={"info"} size={"medium"}/></div>
                    </Tooltip>
                </span>
                <span className={"nav-admin-item color"}>
                    Color
                    <Tooltip content={"Background color of the top level and sub level menu"} position={"top"}>
                        <div className={"headline-info"}><EditorPanelIcon label={"info"} size={"medium"}/></div>
                    </Tooltip>
                </span>
                <span className={"nav-admin-item visibility"}>
                    Visibility
                    <Tooltip content={"Activate menu for all users / assigned groups"} position={"top"}>
                        <div className={"headline-info"}><EditorPanelIcon label={"info"} size={"medium"}/></div>
                    </Tooltip>
                </span>
                <span className={"nav-admin-item actions"}>
                    Actions
                </span>
            </div>
            {menus.length > 0 && menus.map((menu) => {
                return <OverviewMenuEntry key={menu.key} menu={menu}
                                          changeDefaultMenu={changeDefaultMenu} copyMenu={copyMenu}
                                          onDeleteMenu={onDeleteMenu}
                                          onEditMenuMetaData={(menu) => {
                                              // toggle sidebar
                                              if (isSidebarOpen) {
                                                  closeSidebar();
                                              } else {
                                                  openSidebar(menu);
                                              }
                                          }}
                                          groupIds={menu.userGroups ?? []}
                                          allSpaceLabels={allSpaceLabels}/>

            })}
        </div>


        {editMenuMetaData &&
            <ConfigSidebar
                editMenuMetaData={editMenuMetaData}
                saveMenuMetaData={updateMenuData}
                openSidebar={isSidebarOpen}
                closeTrigger={closeSidebar}
                colorChangeTrigger={(color: string, darkTheme: boolean) => {
                    triggerColorChange(editMenuMetaData, color, darkTheme);
                }}
                subMenuBgColorChangeTrigger={(color: string, darkTheme: boolean) => {
                    triggerSubMenuBgColorChange(editMenuMetaData, color, darkTheme);
                }}
                anonymousSettingDisabled={anonymousAccessAssigned && !editMenuMetaData.anonymousAccess}
                groups={editMenuMetaData.userGroups ?? []}
                groupChangeTrigger={((groups) => {
                    triggerGroupsChange(editMenuMetaData, groups);
                })}
                spaceLabels={editMenuMetaData.spaceLabels ?? []}
                spaceLabelsChangeTrigger={((spaceLabels) => {
                    triggerSpaceLabelsChange(editMenuMetaData, spaceLabels);
                })}
                allSpaceLabels={allSpaceLabels}
                activeChangeTrigger={(active: boolean) => {
                    triggerActiveChange(editMenuMetaData, active);
                }}
                anonymousAccessChangeTrigger={(active: boolean) => {
                    triggerAnonymousAccessChange(editMenuMetaData, active);
                }}
            />
        }
    </>
}

async function syncSpaceLabels(menu: Menu, existingSpaceLabels: Label[]) {
    if (!menu.spaceLabels) {
        return;
    }

    const existingMenuLabels = menu.spaceLabels?.filter(menuLabel => {
        return existingSpaceLabels.some(spaceLabel => spaceLabel.id === menuLabel);
    }) ?? [];

    if (existingMenuLabels.length !== menu.spaceLabels.length) {
        menu.spaceLabels = existingMenuLabels;
        await navigationService.updateMenu(menu);
    }
}

export default OverviewPage;
